import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import React from 'react';

import { openNavStyles } from '@/components/modules/app-navigation/open';
import { getSavedItemIcon } from '@/components/modules/app-navigation/open/saved-items';
import { SavedItem } from '@/components/modules/app-navigation/open/saved-items/item';
import { closedNavIconVariants } from '@/components/modules/app-navigation/utils/transitions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { getSavedItemLink } from '@/lib/models/saved-item/utils';

import { closedItemStyles } from '../item-dropdown';

export interface ClosedSavedItemsProps {
  savedItems: SavedItemModel[];
  onDelete: (savedItem: SavedItemModel) => void;
  isOpen?: boolean;
  isTouchDevice: boolean;
  openDropdownMenuId: string | null;
  onSelect: (value: string | null) => void;
  onPointerEnter: (event: React.PointerEvent<HTMLButtonElement | HTMLDivElement>) => void;
  onPointerLeave: (event: React.PointerEvent<HTMLButtonElement | HTMLDivElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>) => void;
}

export const SAVED_ITEMS_MENU_ID = 'saved-items';

const ClosedSavedItems = ({
  savedItems,
  isOpen,
  onDelete,
  onKeyDown,
  onPointerLeave,
  onPointerEnter,
  openDropdownMenuId,
  onSelect,
  isTouchDevice,
}: ClosedSavedItemsProps) => {
  const dropdownMenuProps = !isTouchDevice && { modal: false, open: openDropdownMenuId === SAVED_ITEMS_MENU_ID };

  if (savedItems.length === 0) return null;

  const { icon, trigger } = openNavStyles();
  const { dropdownMenuContent } = closedItemStyles();

  return (
    <DropdownMenu modal={false} {...dropdownMenuProps}>
      <motion.div
        variants={closedNavIconVariants}
        animate={isOpen ? 'hidden' : 'visible'}
        initial={isOpen ? 'hidden' : 'visible'}
      >
        <DropdownMenuTrigger
          className={trigger()}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onKeyDown={onKeyDown}
        >
          <Star className={icon()} />
        </DropdownMenuTrigger>
      </motion.div>
      <DropdownMenuContent
        side="right"
        align="start"
        className={dropdownMenuContent()}
        sideOffset={6}
        alignOffset={-36} // Align the trigger with the first item in the sub items
        onCloseAutoFocus={(event) => event?.preventDefault()}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onEscapeKeyDown={() => onSelect(null)}
        loop
      >
        <DropdownMenuLabel>Favourites</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <ul className="min-w-[240px]">
            {savedItems.map((savedItem, i) => (
              <SavedItem
                key={i}
                label={savedItem.label}
                type={savedItem.type}
                onDelete={() => onDelete(savedItem)}
                href={getSavedItemLink(savedItem)}
                icon={getSavedItemIcon(savedItem)}
                variant="light"
                shouldHideDelete={false}
              />
            ))}
          </ul>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { ClosedSavedItems };
